/**
 * Pure-function flow executor. The worker drives this with the persisted
 * conversation state; we never mutate the graph in place.
 */

import type { FlowGraph, FlowNode, Language, NodeId } from "./schema";

export interface ConversationState {
  flowId: string;
  contactId: string;
  language: Language;
  currentNode: NodeId;
  answers: Record<string, string>;
  tags: Set<string>;
  completed?: boolean;
  unsubscribed?: { reason: string };
}

export interface StepOutput {
  state: ConversationState;
  outboundBody?: string;
  awaitingResponse: boolean;
  effects: (
    | { type: "send"; body: string }
    | { type: "unsubscribe"; reason: string }
    | { type: "escalate"; queue: string; note: string }
    | { type: "complete"; summary?: string }
    | { type: "tag"; add: string[]; remove: string[] }
  )[];
}

function pickLang(t: Record<Language, string>, lang: Language): string {
  return t[lang] ?? t.en;
}

function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/[¡¿.,!?]/g, "");
}

function matchChoice(
  body: string,
  node: Extract<FlowNode, { kind: "question" }>,
  lang: Language,
): string | undefined {
  const n = normalize(body);
  for (const c of node.choices) {
    const label = normalize(pickLang(c.label, lang));
    if (n === label) return c.id;
    if (c.aliases?.some((a) => normalize(a) === n)) return c.id;
    if (label && n.startsWith(label)) return c.id;
  }
  return undefined;
}

/**
 * Advance the conversation. Pass `inbound` when responding to a user reply;
 * omit it to render the initial outbound for a node.
 */
export function step(
  graph: FlowGraph,
  state: ConversationState,
  inbound?: string,
): StepOutput {
  const effects: StepOutput["effects"] = [];
  let cur: FlowNode | undefined = graph.nodes[state.currentNode];
  if (!cur) {
    return { state, awaitingResponse: false, effects };
  }

  // If we're at a question node and have inbound text, record the answer first.
  if (cur.kind === "question" && inbound !== undefined) {
    const choiceId = matchChoice(inbound, cur, state.language);
    if (choiceId) {
      state = { ...state, answers: { ...state.answers, [cur.answerKey]: choiceId } };
      state.currentNode = cur.next ?? state.currentNode;
    } else {
      state.currentNode = cur.fallback ?? cur.next ?? state.currentNode;
    }
    cur = graph.nodes[state.currentNode];
  }

  // Walk forward through non-blocking nodes until we either send a message,
  // need to await a reply, or terminate.
  let guard = 0;
  while (cur && guard++ < 64) {
    switch (cur.kind) {
      case "message": {
        const body = pickLang(cur.body, state.language);
        effects.push({ type: "send", body });
        state = { ...state, currentNode: cur.next ?? state.currentNode };
        cur = cur.next ? graph.nodes[cur.next] : undefined;
        if (!cur) return { state, outboundBody: body, awaitingResponse: false, effects };
        continue;
      }
      case "question": {
        const body = pickLang(cur.prompt, state.language);
        effects.push({ type: "send", body });
        return { state, outboundBody: body, awaitingResponse: true, effects };
      }
      case "branch": {
        const matched = cur.rules.find((r) => {
          const v = state.answers[r.when.answerKey];
          if (v === undefined) return false;
          if (r.when.equals !== undefined) return v === r.when.equals;
          if (r.when.in) return r.when.in.includes(v);
          return false;
        });
        const goto = matched?.goto ?? cur.default;
        state = { ...state, currentNode: goto };
        cur = graph.nodes[goto];
        continue;
      }
      case "tag": {
        const tags = new Set(state.tags);
        cur.add?.forEach((t) => tags.add(t));
        cur.remove?.forEach((t) => tags.delete(t));
        effects.push({ type: "tag", add: cur.add ?? [], remove: cur.remove ?? [] });
        state = { ...state, tags, currentNode: cur.next ?? state.currentNode };
        cur = cur.next ? graph.nodes[cur.next] : undefined;
        continue;
      }
      case "agent_escalation": {
        effects.push({ type: "escalate", queue: cur.queue, note: pickLang(cur.note, state.language) });
        state = { ...state, currentNode: cur.next ?? state.currentNode };
        cur = cur.next ? graph.nodes[cur.next] : undefined;
        continue;
      }
      case "unsubscribe": {
        effects.push({ type: "unsubscribe", reason: cur.reason });
        return {
          state: { ...state, unsubscribed: { reason: cur.reason }, completed: true },
          awaitingResponse: false,
          effects,
        };
      }
      case "completion": {
        effects.push({ type: "complete", summary: cur.summary ? pickLang(cur.summary, state.language) : undefined });
        return {
          state: { ...state, completed: true },
          awaitingResponse: false,
          effects,
        };
      }
    }
  }
  return { state, awaitingResponse: false, effects };
}
